import { useEffect, useMemo, useState, type ReactNode } from "react"
import {
  BarChart3,
  Building2,
  CalendarClock,
  ChevronRight,
  ChevronsUpDown,
  Database,
  FileSignature,
  FileText,
  LayoutDashboard,
  KeyRound,
  LogOut,
  Menu,
  PanelLeft,
  Plus,
  Search,
  Settings,
  Timer,
  TrendingUp,
  Users,
  X,
  type LucideIcon,
} from "lucide-react"
import { Link, useLocation, useNavigate } from "react-router-dom"

import brandMark from "@/assets/itacondo-superadmin-mark.png"
import NotificationBell from "@/components/NotificationBell"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useAuth } from "@/hooks/useAuth"
import { useSuperAdminStats } from "@/hooks/useSuperAdminStats"
import { useVersioning } from "@/hooks/useVersioning"
import { cn } from "@/lib/utils"

interface SuperAdminLayoutProps {
  children: ReactNode
}

interface NavItem {
  label: string
  url: string
  icon: LucideIcon
  count?: number
}

interface NavGroup {
  label: string
  items: NavItem[]
}

const CREATE_ACTIONS = [
  { label: "Nova proposta", url: "/gestao-comercial/propostas?action=new", icon: FileText },
  { label: "Novo contrato", url: "/gestao-comercial/contratos?action=new", icon: FileSignature },
  { label: "Nova organização", url: "/superadmin/organizacoes?nova=1", icon: Building2 },
  { label: "Agendar reunião", url: "/gestao-comercial/reunioes?action=new", icon: CalendarClock },
]

function readCollapsed() {
  try {
    const saved = localStorage.getItem("superadmin-sidebar-collapsed")
    if (saved !== null) return saved === "true"
  } catch {
    /* sem storage: usa o padrão pela largura */
  }
  return window.matchMedia("(max-width: 1199px)").matches
}

export const SuperAdminLayout = ({ children }: SuperAdminLayoutProps) => {
  const { profile, signOut } = useAuth()
  const { data: stats } = useSuperAdminStats()
  const { version } = useVersioning()
  const location = useLocation()
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(readCollapsed)

  useEffect(() => {
    const hadDarkTheme = document.documentElement.classList.contains("dark")
    document.body.classList.add("superadmin-mode")
    document.documentElement.classList.add("dark")
    return () => {
      document.body.classList.remove("superadmin-mode")
      if (!hadDarkTheme) document.documentElement.classList.remove("dark")
    }
  }, [])

  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  useEffect(() => {
    try {
      localStorage.setItem("superadmin-sidebar-collapsed", String(collapsed))
    } catch {
      /* ignora */
    }
  }, [collapsed])

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault()
        setPaletteOpen((open) => !open)
      }
    }
    window.addEventListener("keydown", handleShortcut)
    return () => window.removeEventListener("keydown", handleShortcut)
  }, [])

  const groups = useMemo<NavGroup[]>(() => [
    {
      label: "Plataforma",
      items: [
        { label: "Visão geral", url: "/superadmin", icon: LayoutDashboard },
        { label: "Organizações", url: "/superadmin/organizacoes", icon: Building2, count: stats?.total_condominiums },
        { label: "Usuários", url: "/superadmin/usuarios", icon: Users, count: stats?.active_users },
        { label: "Licenças", url: "/superadmin/licencas", icon: KeyRound },
      ],
    },
    {
      label: "Comercial",
      items: [
        { label: "Painel comercial", url: "/gestao-comercial", icon: TrendingUp },
        { label: "Propostas", url: "/gestao-comercial/propostas", icon: FileText },
        { label: "Contratos", url: "/gestao-comercial/contratos", icon: FileSignature },
        { label: "Reuniões e atas", url: "/gestao-comercial/reunioes", icon: CalendarClock },
        { label: "Horas técnicas", url: "/gestao-comercial/horas", icon: Timer },
        { label: "Métricas", url: "/superadmin/analytics", icon: BarChart3 },
      ],
    },
    {
      label: "Sistema",
      items: [
        { label: "Dados e backups", url: "/superadmin/dados", icon: Database },
        { label: "Configurações", url: "/configuracoes", icon: Settings },
      ],
    },
  ], [stats])

  const allItems = groups.flatMap((group) => group.items.map((item) => ({ ...item, group: group.label })))
  const current = allItems.find((item) => item.url === location.pathname)

  const initials = (profile?.full_name || profile?.email || "SA")
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase()

  const isActive = (url: string) => location.pathname === url

  const go = (url: string) => {
    setPaletteOpen(false)
    navigate(url)
  }

  const userMenu = (trigger: ReactNode) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-60">
        <DropdownMenuLabel className="font-normal">
          <div className="truncate text-sm font-medium">{profile?.full_name || "Super admin"}</div>
          <div className="truncate text-xs text-muted-foreground">{profile?.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate("/configuracoes")}>
          <Settings className="mr-2 h-4 w-4" />Configurações
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => signOut()} className="text-[hsl(var(--destructive))] focus:text-[hsl(var(--destructive))]">
          <LogOut className="mr-2 h-4 w-4" />Sair
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className="px-2 py-1 text-[11px] text-muted-foreground">itaCondo v{version}</div>
      </DropdownMenuContent>
    </DropdownMenu>
  )

  const brand = (
    <div className="flex h-14 shrink-0 items-center gap-2.5 px-4">
      <img src={brandMark} alt="" className="h-7 w-7 rounded-md object-cover" />
      <div className="min-w-0 leading-none">
        <div className="text-[14px] font-semibold tracking-tight text-foreground">itaCondo</div>
        <div className="mt-1 text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">Superadmin</div>
      </div>
      <Button variant="ghost" size="icon" className="ml-auto h-8 w-8 md:hidden" onClick={() => setMobileOpen(false)} aria-label="Fechar menu">
        <X className="h-4 w-4" />
      </Button>
    </div>
  )

  const sidebar = (
    <aside className="flex h-full w-full flex-col border-r border-border bg-[hsl(228_11%_6.5%)]">
      {brand}

      <div className="px-3 pb-2">
        <button
          type="button"
          onClick={() => setPaletteOpen(true)}
          className="flex h-8 w-full items-center gap-2 rounded-md border border-border bg-[hsl(228_8%_9%)] px-2.5 text-[13px] text-muted-foreground transition-colors hover:border-[hsl(228_8%_22%)] hover:text-foreground"
        >
          <Search className="h-3.5 w-3.5" />
          Buscar ou ir para…
          <kbd className="ml-auto rounded border border-border px-1.5 font-sans text-[10px]">Ctrl K</kbd>
        </button>
      </div>

      <nav className="superadmin-scrollbar flex-1 space-y-5 overflow-y-auto px-3 py-3" aria-label="Navegação do superadmin">
        {groups.map((group) => (
          <div key={group.label}>
            <div className="mb-1 px-2.5 text-[11px] font-medium text-[hsl(225_5%_42%)]">{group.label}</div>
            <div className="space-y-px">
              {group.items.map((item) => {
                const Icon = item.icon
                const active = isActive(item.url)
                return (
                  <Link
                    key={item.url}
                    to={item.url}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "group flex h-8 items-center gap-2.5 rounded-md px-2.5 text-[13px] font-medium text-[hsl(225_7%_66%)] transition-colors hover:bg-[hsl(228_8%_11%)] hover:text-foreground",
                      active && "bg-[hsl(228_8%_12.5%)] text-foreground",
                    )}
                  >
                    <Icon className={cn("h-4 w-4 shrink-0 text-[hsl(225_5%_48%)] group-hover:text-foreground", active && "text-[hsl(var(--primary))]")} strokeWidth={1.8} />
                    <span className="truncate">{item.label}</span>
                    {typeof item.count === "number" && (
                      <span className="ml-auto rounded px-1.5 text-[11px] tabular-nums text-[hsl(225_5%_48%)]">{item.count}</span>
                    )}
                  </Link>
                )
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-border p-2">
        {userMenu(
          <button type="button" className="flex w-full items-center gap-2.5 rounded-md p-2 text-left transition-colors hover:bg-[hsl(228_8%_11%)]">
            <Avatar className="h-7 w-7">
              <AvatarFallback className="bg-[hsl(224_60%_22%)] text-[11px] font-semibold text-[hsl(224_100%_85%)]">{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1 leading-tight">
              <div className="truncate text-[13px] font-medium text-foreground">{profile?.full_name || "Super admin"}</div>
              <div className="truncate text-[11px] text-muted-foreground">{profile?.email}</div>
            </div>
            <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" />
          </button>,
        )}
      </div>
    </aside>
  )

  const rail = (
    <aside className="flex h-full w-16 flex-col items-center border-r border-border bg-[hsl(228_11%_6.5%)]">
      <div className="flex h-14 items-center">
        <img src={brandMark} alt="itaCondo" className="h-7 w-7 rounded-md object-cover" />
      </div>
      <nav className="superadmin-scrollbar flex flex-1 flex-col items-center gap-1 overflow-y-auto py-2" aria-label="Navegação compacta do superadmin">
        {groups.map((group, gi) => (
          <div key={group.label} className={cn("flex flex-col items-center gap-1", gi > 0 && "mt-2 border-t border-border pt-3")}>
            {group.items.map((item) => {
              const Icon = item.icon
              return (
                <Tooltip key={item.url} delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Link
                      to={item.url}
                      aria-label={item.label}
                      className={cn(
                        "grid h-9 w-9 place-items-center rounded-md text-[hsl(225_5%_52%)] transition-colors hover:bg-[hsl(228_8%_11%)] hover:text-foreground",
                        isActive(item.url) && "bg-[hsl(228_8%_12.5%)] text-[hsl(var(--primary))]",
                      )}
                    >
                      <Icon className="h-[18px] w-[18px]" strokeWidth={1.8} />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right">{item.label}</TooltipContent>
                </Tooltip>
              )
            })}
          </div>
        ))}
      </nav>
      <div className="py-3">
        {userMenu(
          <button type="button" aria-label="Conta">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-[hsl(224_60%_22%)] text-[11px] font-semibold text-[hsl(224_100%_85%)]">{initials}</AvatarFallback>
            </Avatar>
          </button>,
        )}
      </div>
    </aside>
  )

  return (
    <div className="superadmin-shell dark min-h-screen bg-background text-foreground">
      <div className={cn("fixed inset-y-0 left-0 z-40 hidden md:block", collapsed ? "w-16" : "w-[248px]")}>{collapsed ? rail : sidebar}</div>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <button className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} aria-label="Fechar menu" />
          <div className="relative h-full w-[min(86vw,280px)] shadow-2xl">{sidebar}</div>
        </div>
      )}

      <div className={cn("min-h-screen transition-[padding] duration-200", collapsed ? "md:pl-16" : "md:pl-[248px]")}>
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-border bg-background/85 px-3 backdrop-blur-md sm:px-5">
          <Button variant="ghost" size="icon" className="h-8 w-8 md:hidden" onClick={() => setMobileOpen(true)} aria-label="Abrir menu">
            <Menu className="h-[18px] w-[18px]" />
          </Button>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="hidden h-8 w-8 text-muted-foreground md:inline-flex"
                onClick={() => setCollapsed((value) => !value)}
                aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
              >
                <PanelLeft className="h-[18px] w-[18px]" strokeWidth={1.8} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{collapsed ? "Expandir menu" : "Recolher menu"}</TooltipContent>
          </Tooltip>
          <div className="mx-1 hidden h-4 w-px bg-border md:block" />

          <nav className="flex min-w-0 flex-1 items-center gap-1.5 text-[13px]" aria-label="Breadcrumb">
            <span className="hidden text-muted-foreground sm:inline">{current?.group ?? "Superadmin"}</span>
            <ChevronRight className="hidden h-3.5 w-3.5 text-[hsl(225_5%_36%)] sm:block" />
            <span className="truncate font-medium text-foreground">{current?.label ?? "Superadmin"}</span>
          </nav>

          <div className="ml-auto flex items-center gap-1.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="hidden items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground lg:flex">
                  <span className="h-1.5 w-1.5 rounded-full bg-[hsl(152_60%_52%)]" />
                  Operacional
                </div>
              </TooltipTrigger>
              <TooltipContent>Todos os serviços da plataforma respondendo normalmente</TooltipContent>
            </Tooltip>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground sm:hidden" onClick={() => setPaletteOpen(true)} aria-label="Buscar">
              <Search className="h-4 w-4" />
            </Button>
            <div className="grid h-8 w-8 place-items-center [&_button]:h-8 [&_button]:w-8 [&_button]:p-0">
              <NotificationBell />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="h-8 gap-1.5 px-3 text-[13px] font-medium">
                  <Plus className="h-4 w-4" />
                  <span className="hidden sm:inline">Criar</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                {CREATE_ACTIONS.map((action) => (
                  <DropdownMenuItem key={action.url} onSelect={() => navigate(action.url)}>
                    <action.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                    {action.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="mx-auto w-full max-w-[1440px] px-4 py-5 sm:px-6 lg:px-8 lg:py-7">{children}</main>
      </div>

      <CommandDialog open={paletteOpen} onOpenChange={setPaletteOpen}>
        <CommandInput placeholder="Buscar página ou ação…" />
        <CommandList>
          <CommandEmpty>Nada encontrado.</CommandEmpty>
          <CommandGroup heading="Criar">
            {CREATE_ACTIONS.map((action) => (
              <CommandItem key={action.url} value={action.label} onSelect={() => go(action.url)}>
                <action.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                {action.label}
              </CommandItem>
            ))}
          </CommandGroup>
          <CommandSeparator />
          {groups.map((group) => (
            <CommandGroup key={group.label} heading={group.label}>
              {group.items.map((item) => (
                <CommandItem key={item.url} value={`${group.label} ${item.label}`} onSelect={() => go(item.url)}>
                  <item.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                  {item.label}
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </div>
  )
}
